"use client";
import { useRef } from "react";
import useScrollSpy from "react-use-scrollspy";
import { cn } from "@/lib/utils";
import { TAB_GROUPS } from "./constants";

interface FeatureSectionMessagingProps {
  title?: string;
  description?: string;
  tabGroup?: string;
}
export function FeatureSectionMessaging({
  title = "Connect with your audience",
  description = "Engage your audience individually or at scale with Helios. Send personalized messages to your customers, or send a message to your entire list.",
  tabGroup = "features",
}: FeatureSectionMessagingProps) {
  const sectionRefs = [
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
  ];
  const activeSection = useScrollSpy({
    sectionElementRefs: sectionRefs as React.RefObject<HTMLElement>[],
    offsetPx: -200,
  });
  const handleScroll = (ref: any) => {
    window.scrollTo({
      top: ref.offsetTop - 100,
      left: 0,
      behavior: "smooth",
    });
  };
  return (
    <section>
      <div className="max-w-7xl lg:px-8 sm:py-16 px-4 py-10 mx-auto">
        <div className="md:grid-cols-3 grid items-start grid-cols-1 gap-16">
          <div className="flex flex-col sticky shrink md:top-20 justify-center md:min-h-[calc(100vh-200px)]">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-foreground tracking-tight md:leading-[1.1] text-balance">
              {title}
            </h3>
            <p className="text-muted-foreground text-balance mt-4 leading-normal">{description}</p>
            <ul className="border-border lg:flex flex-col hidden mt-8 border-l">
              {TAB_GROUPS[tabGroup].map((tab, idx) => (
                <li
                  key={tab.id}
                  onClick={() => handleScroll(sectionRefs[idx].current)}
                  className={cn(
                    activeSection === idx
                      ? "border-primary bg-muted/50"
                      : "border-transparent text-muted-foreground",
                    "flex p-4 md:p-5 font-medium tracking-tight -translate-x-[1px] border-l-4 cursor-pointer hover:text-foreground transition-all duration-75",
                  )}
                >
                  {tab.title}
                </li>
              ))}
            </ul>
          </div>
          <div className="md:pb-20 flex flex-col col-span-2">
            {TAB_GROUPS[tabGroup].map((tab, idx) => (
              <div
                key={tab.id}
                ref={sectionRefs[idx]}
                className="bg-muted rounded-xl lg:grid-cols-16 md:gap-12 md:p-10 grid w-full grid-cols-1 gap-4 p-8 mb-4"
              >
                {tab.component ? <tab.component tab={tab} /> : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
